import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, CardContent, Typography, TextField, Button, Grid, Box,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, Chip, FormControl, InputLabel, Select, MenuItem, Link, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth.context';
import { maskCPF2 } from '../../utils/mask';

const STATUS_OPTIONS = ['Rascunho', 'Pendente', 'Aprovado', 'Recusado', 'Pago'];

const formatBRL = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const number = Number(value);
  if (isNaN(number)) return '';
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const parseBRL = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const maskCurrency = (value) => {
  const digits = String(value).replace(/\D/g, '');
  const number = parseInt(digits || '0', 10) / 100;
  return number.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const getReceiptUrl = (proposalId, expenseId) => {
  if (!proposalId || !expenseId) return null;
  return `${api.defaults.baseURL}/reimbursement-proposals/${proposalId}/receipt/${expenseId}`;
};

const isImage = (mime) => mime?.startsWith('image/');
const isPdf = (mime) => mime === 'application/pdf';

const emptyExpense = () => ({
  id: `${Date.now()}-${Math.random()}`,
  description: '',
  amount: '',
  receipt: null
});

const emptyProposal = () => ({
  id: null,
  description: '',
  proponent_name: '',
  proponent_role: '',
  proponent_document: '',
  status: 'Rascunho',
  expenses: [emptyExpense()]
});

export default function PrestacaoContas() {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProposal());
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: null, mime: null, name: '' });

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get('/users/me?populate=*');
      setForm(prev => ({
        ...prev,
        proponent_name: data.name || data.username || '',
        proponent_document: data.document || data.cpf || ''
      }));
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
    }
  }, []);

  const loadProposals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reimbursement-proposals?populate[expenses][populate]=receipt&sort=createdAt:desc');
      setProposals(data.data || []);
    } catch (err) {
      setError('Erro ao carregar propostas: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
    loadProposals();
  }, [loadUser, loadProposals]);

  const handleOpenForm = (proposal = null) => {
    if (proposal) {
      const attributes = proposal.attributes || proposal;
      setEditing(proposal.id);
      setForm({
        id: proposal.id,
        description: attributes.description || '',
        proponent_name: attributes.proponent_name || '',
        proponent_role: attributes.proponent_role || '',
        proponent_document: attributes.proponent_document || '',
        status: attributes.status || 'Rascunho',
        expenses: (attributes.expenses || []).map((exp, idx) => ({
          id: exp.id || `${Date.now()}-${idx}`,
          description: exp.description || '',
          amount: exp.amount ? maskCurrency(exp.amount) : '',
          receipt: exp.receipt?.data || exp.receipt || null
        }))
      });
    } else {
      setEditing(null);
      setForm(emptyProposal());
      loadUser();
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyProposal());
  };

  const handleAddExpense = () => {
    setForm(prev => ({ ...prev, expenses: [...prev.expenses, emptyExpense()] }));
  };

  const handleRemoveExpense = (expenseId) => {
    setForm(prev => ({
      ...prev,
      expenses: prev.expenses.filter(e => e.id !== expenseId)
    }));
  };

  const handleExpenseChange = (expenseId, field, value) => {
    setForm(prev => ({
      ...prev,
      expenses: prev.expenses.map(e => {
        if (e.id !== expenseId) return e;
        if (field === 'amount') {
          return { ...e, [field]: maskCurrency(value) };
        }
        return { ...e, [field]: value };
      })
    }));
  };

  const handleFileChange = async (expenseId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('files', file);
    formData.append('private', 'true');
    formData.append('folderPath', 'receipts/private');

    try {
      const { data } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploaded = Array.isArray(data) ? data[0] : data;
      handleExpenseChange(expenseId, 'receipt', uploaded);
      setSuccess('Comprovante enviado com sucesso');
    } catch (err) {
      setError('Erro ao enviar comprovante: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const getMimeFromResponse = (response, fallbackMime) => {
    const contentType = response?.headers?.['content-type'];
    if (contentType) {
      return contentType.split(';')[0].trim();
    }
    return fallbackMime || 'application/octet-stream';
  };

  const handlePreview = async (proposalId, expenseId, fileName, mime) => {
    try {
      const url = getReceiptUrl(proposalId, expenseId);
      const response = await api.get(url, { responseType: 'blob' });
      const detectedMime = getMimeFromResponse(response, mime);
      const blob = new Blob([response.data], { type: detectedMime });
      const blobUrl = URL.createObjectURL(blob);
      setPreview({ open: true, url: blobUrl, mime: detectedMime, name: fileName });
    } catch (err) {
      setError('Erro ao carregar comprovante: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleClosePreview = () => {
    if (preview.url) URL.revokeObjectURL(preview.url);
    setPreview({ open: false, url: null, mime: null, name: '' });
  };

  const totalAmount = useMemo(() => {
    return form.expenses.reduce((sum, exp) => sum + parseBRL(exp.amount), 0);
  }, [form.expenses]);

  const buildPayload = () => ({
    description: form.description,
    proponent_name: form.proponent_name,
    proponent_role: form.proponent_role,
    proponent_document: form.proponent_document,
    status: form.status,
    expenses: form.expenses.map(exp => ({
      id: typeof exp.id === 'number' ? exp.id : undefined,
      description: exp.description,
      amount: parseBRL(exp.amount),
      receipt: exp.receipt?.id || null
    }))
  });

  const handleSave = async () => {
    if (!form.proponent_name || !form.proponent_role || !form.proponent_document) {
      setError('Preencha nome, cargo e documento do proponente');
      return;
    }

    if (form.expenses.length === 0 || form.expenses.some(e => !e.description || !e.amount)) {
      setError('Preencha descrição e valor de todas as despesas');
      return;
    }

    try {
      const payload = buildPayload();
      if (editing) {
        await api.put(`/reimbursement-proposals/${editing}`, { data: payload });
        setSuccess('Proposta atualizada com sucesso');
      } else {
        await api.post('/reimbursement-proposals', { data: payload });
        setSuccess('Proposta criada com sucesso');
      }
      handleCloseForm();
      loadProposals();
    } catch (err) {
      setError('Erro ao salvar proposta: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reimbursement-proposals/${id}`);
      setSuccess('Proposta excluída com sucesso');
      setDeleteDialog(null);
      loadProposals();
    } catch (err) {
      setError('Erro ao excluir proposta: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Rascunho': return 'default';
      case 'Pendente': return 'warning';
      case 'Aprovado': return 'info';
      case 'Pago': return 'success';
      case 'Recusado': return 'error';
      default: return 'default';
    }
  };

  return (
    <Card sx={{ margin: 'auto', mt: 5, mx: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Prestação de Contas</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
          >
            Nova Proposta
          </Button>
        </Box>

        {proposals.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nenhuma proposta de reembolso encontrada.
          </Typography>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nº</TableCell>
                <TableCell>Proponente</TableCell>
                <TableCell>Cargo</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.map((item) => {
                const attr = item.attributes || item;
                return (
                  <TableRow key={item.id}>
                    <TableCell>{attr.proposal_number}</TableCell>
                    <TableCell>{attr.proponent_name}</TableCell>
                    <TableCell>{attr.proponent_role}</TableCell>
                    <TableCell>{attr.proponent_document}</TableCell>
                    <TableCell>{formatBRL(attr.total_amount)}</TableCell>
                    <TableCell>
                      <Chip label={attr.status} color={getStatusColor(attr.status)} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <>
                        <IconButton color="primary" onClick={() => handleOpenForm(item)} title="Editar">
                          <EditIcon />
                        </IconButton>
                        {attr.status === 'Rascunho' && (
                          <IconButton color="error" onClick={() => setDeleteDialog(item)} title="Excluir">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="lg" fullWidth>
          <DialogTitle>{editing ? 'Editar Proposta' : 'Nova Proposta de Reembolso'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Nome do Proponente"
                  value={form.proponent_name}
                  onChange={(e) => setForm(prev => ({ ...prev, proponent_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cargo"
                  value={form.proponent_role}
                  onChange={(e) => setForm(prev => ({ ...prev, proponent_role: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Documento (CPF)"
                  value={form.proponent_document}
                  onChange={(e) => setForm(prev => ({ ...prev, proponent_document: maskCPF2(e.target.value) }))}
                  inputProps={{ maxLength: 14 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    value={form.status}
                    label="Status"
                    onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Descrição Livre"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Despesas</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell width="180px">Valor (R$)</TableCell>
                    <TableCell width="280px">Comprovante</TableCell>
                    <TableCell width="80px" align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.expenses.map((exp) => (
                    <TableRow key={exp.id}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exp.description}
                          onChange={(e) => handleExpenseChange(exp.id, 'description', e.target.value)}
                          placeholder="Descrição da despesa"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={exp.amount}
                          onChange={(e) => handleExpenseChange(exp.id, 'amount', e.target.value)}
                          placeholder="0,00"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            startIcon={<AttachFileIcon />}
                          >
                            {exp.receipt ? 'Trocar' : 'Anexar'}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileChange(exp.id, e.target.files[0])}
                            />
                          </Button>
                          {exp.receipt && (
                            <>
                              <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                                {exp.receipt.name || 'Comprovante'}
                              </Typography>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handlePreview(form.id, typeof exp.id === 'number' ? exp.id : null, exp.receipt.name || 'Comprovante', exp.receipt.mime)}
                                title="Visualizar comprovante"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => handleRemoveExpense(exp.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddExpense}
              sx={{ mt: 1 }}
            >
              Adicionar Despesa
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography variant="h6">
                Total: {formatBRL(totalAmount)}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} startIcon={<CancelIcon />}>Cancelar</Button>
            <Button onClick={handleSave} variant="contained" color="success" startIcon={<SaveIcon />}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
          <DialogTitle>Excluir proposta?</DialogTitle>
          <DialogContent>
            Deseja excluir a proposta nº {deleteDialog?.attributes?.proposal_number || deleteDialog?.proposal_number}?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(null)}>Cancelar</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => handleDelete(deleteDialog.id)}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={preview.open} onClose={handleClosePreview} maxWidth="lg" fullWidth>
          <DialogTitle>{preview.name}</DialogTitle>
          <DialogContent sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column' }}>
            {preview.url && isImage(preview.mime) && (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                <img
                  src={preview.url}
                  alt={preview.name}
                  style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                />
              </Box>
            )}
            {preview.url && isPdf(preview.mime) && (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                <iframe
                  src={preview.url}
                  title={preview.name}
                  width="100%"
                  height="575vh"
                  style={{ border: 'none' }}
                />
              </Box>
            )}
            {preview.url && !isImage(preview.mime) && !isPdf(preview.mime) && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Este tipo de arquivo não pode ser pré-visualizado. Faça o download para visualizar.</Typography>
              </Box>
            )}
            {!preview.url && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePreview}>Fechar</Button>
            {preview.url && (
              <Button
                component="a"
                href={preview.url}
                download={preview.name}
                color="primary"
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}
